import React from 'react';
import { createContext, useContext, useState, useEffect } from "react";
import { useFirestore } from './FirestoreContext.jsx';
import { useAuth } from './AuthContext.jsx';
import { useApp } from './AppContext.jsx';
import { useTranslation } from "react-i18next";
import { createLocalStore } from "@MyLib/System/LocalStorage.js";
import { defaultFilters, createDeckField, createCard, createColumn } from './SaveData.js';

const SaveContext = createContext();

const useLocalStore = () => {
	const save = createLocalStore({root: "MTGDeck/"});

	const SaveData = "Local/SaveData";
	const DeckField = "Store/DeckField";
	const DeckList = "Store/DeckList";

	return {
		saveData: (key, value) => { save.saveData(`${SaveData}/${key}`, value); },
		loadData: (key) => { return save.loadData(`${SaveData}/${key}`); },
		saveJson: (key, value) => { save.saveJson(`${SaveData}/${key}`, value); },
		loadJson: (key) => { return save.loadJson(`${SaveData}/${key}`); },

		saveDeckField: (value) => { save.saveJson(DeckField, value); },
		loadDeckField: () => { return save.loadJson(DeckField); },
		deleteDeckField: () => { save.deleteData(DeckField); },

		saveDeck: (value) => { save.saveJson(`${DeckList}/${value.id}`, value); },
		loadDeck: (deckId) => { return save.loadJson(`${DeckList}/${deckId}`); },
		deleteDeck: (deckId) => { save.deleteData(`${DeckList}/${deckId}`); },
	};
};

export const SaveProvider = ({children}) =>
{
	const { t } = useTranslation();
	const {logout} = useAuth();
	const store = useFirestore();
	const local = useLocalStore();
	const {setYesNoModal} = useApp();

	// Local/SaveData
	const [cardDisplaySize, setCardDisplaySize] = useState(() => {
		return local.loadData("CardDisplaySize") ?? "sm";
	});
	const [filters, setFilters] = useState(() => ({
		...defaultFilters,
		...(local.loadJson("Filters") ?? {})
	}));

	useEffect(() => {
		local.saveData("CardDisplaySize", cardDisplaySize);
	}, [cardDisplaySize]);
	useEffect(() => {
		local.saveJson("Filters", filters);
	}, [filters]);

	// deck
	const [savedDeck, setSavedDeck] = useState(null);
	const [buildDeck, setBuildDeck] = useState(null);

	const setBuildCard = (card) => {
		const columnIndex = buildDeck.columns.findIndex(col =>
			col.cards.some(cd => cd.id === card.id)
		);
		{
			const newCards = [...buildDeck.columns[columnIndex].cards];
			const cardIndex = newCards.findIndex(c => c.id === card.id);
			newCards[cardIndex] = card;
			const newCards2 = newCards.filter(c => c.num >= 0);

			const newDeck = {
				...buildDeck,
				columns: buildDeck.columns.map((col, index) => {
					if (index !== columnIndex) {
						return col;
					}
					return {
						...col,
						cards: newCards2
					};
				})
			};
			setBuildDeck(newDeck);
		}
	};
	const addBuildCards = (cards, columnIndex) => {
		if (cards.length == 0) {
			return buildDeck;
		}

		const newCards = cards.map((item) => {
			return {
				...createCard(),
				uuid: item.uuid,
				num: item.num ?? 1,
				language: item.language
			};
		});

		if (buildDeck.columns.length == 0) {
			buildDeck.columns =[createColumn()];
		}
		const newDeck = {
			...buildDeck,
			columns: buildDeck.columns.map((col, index) => {
				if (index !== columnIndex) {
					return col;
				}
				return {
					...col,
					cards: [
						...col.cards,
						...newCards
					]
				};
			})
		};
		setBuildDeck(newDeck);
		return newDeck;
	};
	const updateBuildColumnName = (columnIndex, name) => {
		const newDeck = {
			...buildDeck,
			columns: buildDeck.columns.map((col, index) => {
				if (index !== columnIndex) {
					return col;
				}
				return {
					...col,
					name
				};
			})
		};
		setBuildDeck(newDeck);
	};

	// DeckField
	const [deckField, setDeckField] = useState(() => {
		return local.loadDeckField() ?? createDeckField();
	});

	// Deck
	const saveDeck = async (deck) => {
		const newDeck = {...deck, date: Date.now()};
		await store.saveDeck(newDeck);
		local.saveDeck(newDeck);
		setSavedDeck(newDeck);
		setBuildDeck(newDeck);
	};
	const saveDeckLocal = () => {
		if (buildDeck) {
			const newDeck = {...buildDeck, date: Date.now()};
			local.saveDeck(newDeck);
		}
	};
	const loadDeck = async (deckId) => {
		let deck = null;
		const storeDeck = await store.loadDeck(deckId);
		if (storeDeck.status == 200) {
			deck = storeDeck.data;
		}
		const localDeck = local.loadDeck(deckId);
		if (localDeck) {
			if (!deck || deck.date < localDeck.date) {
				deck = localDeck;
			}
		}

		if (deck) {
			saveDeckLocal();
			setSavedDeck(deck);
			setBuildDeck(deck);
		}
		return deck;
	}
	const deleteDeck = async (deckId) => {
		await store.deleteDeck(deckId);
		local.deleteDeck(deckId);
		if (buildDeck && buildDeck.id == deckId)
		{
			setSavedDeck(null);
			setBuildDeck(null);
		}
	};

	// DeckField
	const saveDeckList = async (deckList) => {
		const newDackField = {
			...deckField,
			date: Date.now(),
			deckList: [...deckList]
		};
		
		local.saveDeckField(newDackField);
		await store.saveDeckField(newDackField);
		setDeckField(newDackField);
	};
	const loadDeckList = async () => {
		const res = await store.loadDeckField();
		if (res.status == 200) {
			const body = res.data ?? defaultDeckField;
			if (deckField.date == 0)
			{
				local.saveDeckField(body);
				setDeckField(body);
			}
			else if (deckField.id == body.id) {
				if (deckField.date > body.date) {
					setYesNoModal({
						title: t("SaveLocalTitle"),
						body: <div>{t("SaveLocalDesc")}</div>,
						onClose: () => {
							setYesNoModal(null);
						},
						onSubmit: async () => {
							// サーバデータ使用
							// ローカルデッキデッキデータ削除予定
							local.saveDeckField(body);
							setDeckField(body);
							setBuildDeck(null);
						},
						onCancel: async () => {
							// ローカルデータ使用
							// サーバデッキデータ削除予定
						}
					});
				}
				else {
					setDeckField(body);
				}
			}
			else {
				setYesNoModal({
					title: t("SaveStoreTitle"),
					body: <div>{t("SaveStoreDesc")}</div>,
					onClose: () => {
						setYesNoModal(null);
					},
					onSubmit: () => {
						// サーバデータ使用
						// ローカルデッキデッキデータ削除予定
						local.saveDeckField(body);
						setDeckField(body);
						setBuildDeck(null);
					},
					onCancel: async () => {
						await logout();
					}
				});
			}
		}
	};
	
	useEffect(() => {
		const handler = () => {
			saveDeckLocal();
		};
		
		window.addEventListener("beforeunload", handler);
		return () => {
			window.removeEventListener("beforeunload", handler);
		};
	}, [buildDeck]);

	return (
		<SaveContext.Provider
			value={{
				cardDisplaySize,
				setCardDisplaySize,
				filters,
				setFilters,

				download: (deck) => {
					const json = JSON.stringify(deck, null, '\t');
					const blob = new Blob([json], { type: 'application/json' });
					const url = URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = `${deck.name}.json`;
					a.click();
					URL.revokeObjectURL(url);
				},
				open: () => {
					return new Promise((resolve, reject) => {
						const input = document.createElement('input');
						input.type = 'file';
						input.accept = '.json';
						input.onchange = e => {
							const file = e.target.files[0];
							if (!file) {
								return;
							}
							const reader = new FileReader();
							reader.onload = e => {
								try {
									const data = JSON.parse(e.target.result);
									resolve(data);
								} catch (err) {
									reject(new Error("JSONのパースに失敗しました"));
								}
							};
							reader.onerror = () => reject(new Error("ファイルの読み込みに失敗しました"));
							reader.readAsText(file);
						};
						input.oncancel = () => {
							resolve(null); // rejectではなくresolve(null)にすると、エラーにならずに「なし」として扱える
						};
						input.click();
					});
				},

				buildDeck,
				setBuildDeck,

				setBuildCard,
				updateBuildColumnName,
				addBuildCards,

				deckList: deckField.deckList,
				saveDeckList,
				loadDeckList,

				saveDeck,
				loadDeck,
				deleteDeck,

				clearSave: () => {
					setDeckField(createDeckField());
					setBuildDeck(null);
				},
				deleteLocal: () => {
					deckField.deckList.forEach(item => {
						local.deleteDeck(item.id);
					});
					local.deleteDeckField();
				},
				deleteStore: async () => {
					await store.deleteAll();
				}
			}}
		>
			{children}
		</SaveContext.Provider>
	);
};

export const useSave = () => useContext(SaveContext);

export const getCardDisplaySize = () => {
	const {cardDisplaySize} = useSave();
	switch (cardDisplaySize)
	{
		case "md": return "80px";
		case "lg": return "200px";
	}
	return "30px";
}
